import { Clock, Eye, UserCheck, UserPlus, Users, UserX } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  ConfirmModal,
  DataTable,
  FilterBar,
  PageHeader,
  Paginacion,
  TarjetasDeEstado,
} from "../../components/ui/patrones";
import { Aviso, Boton, Campo, Etiqueta, Modal } from "../../components/ui/primitivos";
import {
  cambiarEstadoUsuario,
  cancelarInvitacion,
  type EstadoEnvio,
  fetchEstadoEnvio,
  fetchInvitaciones,
  fetchRolesAsignables,
  fetchUsuarios,
  type Invitacion,
  invitar,
  reenviarInvitacion,
  type Usuario,
} from "../../lib/consola-api";
import { type Orden, siguienteOrden } from "../../lib/orden";
import { paginar, paginaValida } from "../../lib/paginacion";
import { useSession } from "../../lib/SessionContext";
import { faltan } from "../../lib/tiempo";
import { useDatos } from "../../lib/useDatos";
import {
  contarPorEstado,
  type EstadoDeMiembro,
  type Miembro,
  ordenarMiembros,
  unificarMiembros,
} from "./miembros";

/**
 * Usuarios — porteada de `UsersListPage` de `imprenta`.
 *
 * Lista UNIFICADA de miembros e invitaciones pendientes, como allá: para quien
 * administra son la misma pregunta —"¿quién tiene acceso?"— y separarlas en dos
 * pantallas obliga a mirar en dos lados para responderla.
 *
 * Lo que el listado muestra ya viene filtrado por CONTENCIÓN desde el worker
 * (ADR 0015): un Manager no ve al Administrador. Acá no se filtra de nuevo —
 * hacerlo daría la ilusión de que la UI protege algo.
 */

const ESTADO_INVITACION: Record<
  string,
  { texto: string; tono: "ok" | "aviso" | "error" | "neutral" }
> = {
  pending: { texto: "Pendiente", tono: "aviso" },
  expirada: { texto: "Expirada", tono: "neutral" },
  canceled: { texto: "Cancelada", tono: "error" },
  accepted: { texto: "Aceptada", tono: "ok" },
  rejected: { texto: "Rechazada", tono: "error" },
};

export function UsuariosPagina() {
  const { can, me } = useSession();
  const navegar = useNavigate();
  const usuarios = useDatos(fetchUsuarios);
  const invitaciones = useDatos(fetchInvitaciones);
  const asignables = useDatos(fetchRolesAsignables);

  const [busqueda, setBusqueda] = useState("");
  const [invitando, setInvitando] = useState(false);
  /**
   * Abrir una persona es NAVEGAR, no montar un modal (ADR 0064/0067).
   *
   * Con ruta propia el usuario tiene URL, y la pantalla puede mostrar el estado
   * y el último ingreso — que en el modal no entraban.
   */
  const abrirUsuario = (u: Usuario) => navegar(`/usuarios/${u.id}`);
  const [error, setError] = useState<string | null>(null);
  const [estado, setEstado] = useState<EstadoDeMiembro | "">("");
  const [orden, setOrden] = useState<Orden | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamanoPagina, setTamanoPagina] = useState(10);
  // Desactivar a alguien y cancelar una invitación piden confirmación, como
  // allá. Antes bastaba UN click para dejar a una persona sin acceso.
  const [aCambiarEstado, setACambiarEstado] = useState<Miembro | null>(null);
  const [aCancelar, setACancelar] = useState<Miembro | null>(null);

  const puedeInvitar = can("usuarios", "create");
  const puedeEditar = can("usuarios", "update");
  const puedeDesactivar = can("usuarios", "delete");

  /**
   * La lista completa, sin filtrar: es la que cuentan las tarjetas. Filtrar
   * antes haría que «Inactivos: 3» cambiara al escribir en la búsqueda.
   */
  const todos = useMemo<Miembro[]>(
    () =>
      unificarMiembros(
        usuarios.datos ?? [],
        (invitaciones.datos ?? []).filter((i) => i.estado === "pending" || i.estado === "expirada"),
      ),
    [usuarios.datos, invitaciones.datos],
  );

  const conteo = contarPorEstado(todos);

  const filtrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return todos.filter((m) => {
      if (estado && m.estado !== estado) return false;
      if (!texto) return true;
      return [m.nombre, m.email, m.roles.join(" ")].some((c) => c.toLowerCase().includes(texto));
    });
  }, [todos, busqueda, estado]);

  const filas = ordenarMiembros(filtrados, orden);
  const paginaActual = paginaValida(pagina, filas.length, tamanoPagina);
  const enPantalla = paginar(filas, paginaActual, tamanoPagina);

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await Promise.all([usuarios.recargar(), invitaciones.recargar()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Usuarios"
        subtitulo="Accesos al sistema: miembros e invitaciones pendientes"
        acciones={
          puedeInvitar ? (
            <Boton onClick={() => setInvitando(true)}>
              <UserPlus className="mr-2 inline h-4 w-4" aria-hidden="true" />
              Invitar Usuario
            </Boton>
          ) : undefined
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}
      {usuarios.error ? <Aviso>{usuarios.error}</Aviso> : null}

      {/* Las cuatro tarjetas de allá, que FILTRAN por estado. Cuentan sobre la
          lista completa: si contaran sobre la filtrada, «Inactivos: 3» cambiaría
          al escribir en la búsqueda. */}
      <TarjetasDeEstado
        tarjetas={[
          {
            clave: "todos",
            titulo: "Todos",
            valor: conteo.todos,
            tono: "neutro",
            activa: estado === "",
            alClickear: () => setEstado(""),
          },
          {
            clave: "activo",
            titulo: "Activos",
            valor: conteo.activo,
            tono: "verde",
            activa: estado === "activo",
            alClickear: () => setEstado(estado === "activo" ? "" : "activo"),
          },
          {
            clave: "pendiente",
            titulo: "Pendientes",
            valor: conteo.pendiente,
            tono: "ambar",
            activa: estado === "pendiente",
            alClickear: () => setEstado(estado === "pendiente" ? "" : "pendiente"),
          },
          {
            clave: "inactivo",
            titulo: "Inactivos",
            valor: conteo.inactivo,
            tono: "rojo",
            activa: estado === "inactivo",
            alClickear: () => setEstado(estado === "inactivo" ? "" : "inactivo"),
          },
        ]}
      />

      <FilterBar
        busqueda={
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, email o rol..."
            aria-label="Buscar"
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          />
        }
        filtrosActivos={busqueda ? 1 : 0}
        alLimpiar={() => setBusqueda("")}
      />

      {/* El listado mezcla usuarios e invitaciones pendientes, así que cada
          columna decide según el tipo de fila. Van juntos a propósito: son las
          dos formas de "alguien con acceso", y separarlos en dos tablas haría
          que una invitación olvidada no se vea. */}
      <DataTable
        datos={enPantalla}
        clave={(m) => m.clave}
        cargando={usuarios.cargando}
        mensajeVacio={busqueda || estado ? "Nadie coincide con los filtros" : "No hay usuarios"}
        orden={orden}
        alOrdenar={(campo) => setOrden((o) => siguienteOrden(o, campo))}
        columnas={[
          {
            clave: "nombre",
            encabezado: "Usuario",
            ordenarPor: "nombre",
            className: "font-medium",
            // Avatar + nombre arriba, email abajo: es UNA columna, como allá.
            // Dos columnas separadas hacen leer en zigzag para saber de quién
            // es cada mail.
            render: (m) => <CeldaUsuario miembro={m} esUnoMismo={m.usuario?.id === me?.id} />,
          },
          {
            clave: "roles",
            encabezado: "Rol",
            ordenarPor: "rol",
            render: (m) =>
              m.roles.length > 0 ? (
                <span className="flex flex-wrap gap-1">
                  {m.roles.map((rol) => (
                    <Etiqueta key={rol}>{rol}</Etiqueta>
                  ))}
                </span>
              ) : (
                // «Sin rol» y no un guion: alguien sin rol no ve nada del
                // sistema, y eso es una condición, no un dato que falta.
                <Etiqueta tono="aviso">Sin rol</Etiqueta>
              ),
          },
          {
            clave: "estado",
            encabezado: "Estado",
            ordenarPor: "estado",
            render: (m) =>
              m.clase === "usuario" ? (
                <Etiqueta tono={m.estado === "activo" ? "ok" : "error"}>
                  {m.estado === "activo" ? "Activo" : "Inactivo"}
                </Etiqueta>
              ) : (
                <Etiqueta tono={ESTADO_INVITACION[m.invitacion?.estado ?? ""]?.tono ?? "neutral"}>
                  {ESTADO_INVITACION[m.invitacion?.estado ?? ""]?.texto ??
                    m.invitacion?.estado ??
                    "—"}
                </Etiqueta>
              ),
          },
          {
            clave: "acciones",
            encabezado: "",
            className: "text-right",
            render: (m) =>
              m.clase === "usuario" ? (
                <div className="flex justify-end gap-1">
                  {puedeEditar && m.usuario ? (
                    <Boton
                      variante="fantasma"
                      tamano="icono"
                      aria-label={`Ver ${m.usuario.nombre}`}
                      title="Ver"
                      onClick={() => abrirUsuario(m.usuario as Usuario)}
                    >
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    </Boton>
                  ) : null}
                  {/* Nadie se desactiva a sí mismo (ADR 0019): el servidor lo
                      rechaza, y ofrecerlo sería prometer algo que no pasa. */}
                  {puedeDesactivar && m.usuario && m.usuario.id !== me?.id ? (
                    <Boton
                      variante="fantasma"
                      tamano="icono"
                      className={
                        m.estado === "activo"
                          ? "text-red-600 hover:bg-red-50 hover:text-red-700"
                          : undefined
                      }
                      aria-label={`${m.estado === "activo" ? "Desactivar" : "Activar"} ${m.usuario.nombre}`}
                      title={m.estado === "activo" ? "Desactivar" : "Activar"}
                      onClick={() => setACambiarEstado(m)}
                    >
                      {m.estado === "activo" ? (
                        <UserX className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <UserCheck className="h-4 w-4" aria-hidden="true" />
                      )}
                    </Boton>
                  ) : null}
                </div>
              ) : puedeInvitar && m.invitacion ? (
                <div className="flex justify-end gap-2">
                  <Boton
                    variante="fantasma"
                    onClick={() =>
                      accion(() => reenviarInvitacion((m.invitacion as Invitacion).id))
                    }
                  >
                    Reenviar
                  </Boton>
                  <Boton variante="peligro" onClick={() => setACancelar(m)}>
                    Cancelar
                  </Boton>
                </div>
              ) : null,
          },
        ]}
      />

      {filas.length > 0 ? (
        <Paginacion
          pagina={paginaActual}
          tamano={tamanoPagina}
          total={filas.length}
          alCambiarPagina={setPagina}
          alCambiarTamano={(t) => {
            setTamanoPagina(t);
            setPagina(1);
          }}
        />
      ) : null}

      <ConfirmModal
        abierto={aCambiarEstado !== null}
        onCerrar={() => setACambiarEstado(null)}
        onConfirmar={() => {
          const usuario = aCambiarEstado?.usuario;
          const activar = aCambiarEstado?.estado !== "activo";
          if (usuario) void accion(() => cambiarEstadoUsuario(usuario.id, activar));
        }}
        titulo={aCambiarEstado?.estado === "activo" ? "Desactivar usuario" : "Activar usuario"}
        descripcion={
          aCambiarEstado?.estado === "activo"
            ? `¿Desactivar a ${aCambiarEstado?.email}? No va a poder entrar al sistema.`
            : `¿Activar a ${aCambiarEstado?.email}? Va a poder entrar de nuevo.`
        }
        textoConfirmar={aCambiarEstado?.estado === "activo" ? "Desactivar" : "Activar"}
        variante={aCambiarEstado?.estado === "activo" ? "destructiva" : "normal"}
      />

      <ConfirmModal
        abierto={aCancelar !== null}
        onCerrar={() => setACancelar(null)}
        onConfirmar={() => {
          const inv = aCancelar?.invitacion;
          if (inv) void accion(() => cancelarInvitacion(inv.id));
        }}
        titulo="Cancelar invitación"
        descripcion={`¿Cancelar la invitación a ${aCancelar?.email}? El enlace deja de servir.`}
        textoConfirmar="Cancelar invitación"
        textoCancelar="Volver"
        variante="destructiva"
      />

      {invitando ? (
        <ModalInvitar
          rolesAsignables={asignables.datos ?? []}
          onCerrar={() => setInvitando(false)}
          onListaDesactualizada={() => invitaciones.recargar()}
        />
      ) : null}
    </div>
  );
}

/**
 * Invitación.
 *
 * El select ofrece solo los roles que el worker va a aceptar
 * (`/roles-asignables`, filtrado por contención). Es conveniencia, no control:
 * el rechazo del servidor sigue estando igual (ADR 0019).
 *
 * Y **espera el estado terminal del envío** en vez de mostrar un OK optimista
 * (ADR 0005). No es polling: es una espera acotada que termina sí o sí.
 */
function ModalInvitar({
  rolesAsignables,
  onCerrar,
  onListaDesactualizada,
}: {
  rolesAsignables: string[];
  onCerrar: () => void;
  onListaDesactualizada: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [rol, setRol] = useState(rolesAsignables[0] ?? "");
  const [enviando, setEnviando] = useState(false);
  const [envio, setEnvio] = useState<EstadoEnvio | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function enviar() {
    setEnviando(true);
    setError(null);
    try {
      const { envio: inicial } = await invitar({ email: email.trim(), rol });
      let estado = inicial;
      setEnvio(estado);

      // Si quedó pendiente, se consulta hasta que sea terminal. Tope corto: si
      // no resolvió, el cron lo retoma y el listado la muestra igual.
      if (estado && estado.estado === "pendiente") {
        for (let i = 0; i < 5; i++) {
          await new Promise((r) => setTimeout(r, 1000));
          estado = await fetchEstadoEnvio(estado.id);
          setEnvio(estado);
          if (estado.estado !== "pendiente") break;
        }
      }

      // La invitación YA está creada, pase lo que pase con el mail: el listado
      // se refresca siempre.
      await onListaDesactualizada();

      // Y el modal solo se cierra si el mail salió. Si falló, queda abierto con
      // el motivo — cerrarlo dejaría al usuario creyendo que la persona recibió
      // algo, que es exactamente el OK optimista que el outbox vino a evitar
      // (ADR 0005). Desde el listado puede reenviar.
      if (estado === null || estado.estado === "enviado") onCerrar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo invitar");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal abierto onCerrar={onCerrar} titulo="Nueva invitación">
      <div className="space-y-4">
        {error ? <Aviso>{error}</Aviso> : null}
        {envio?.estado === "fallido" ? (
          <Aviso>El mail no se pudo entregar: {envio.ultimoError ?? "error desconocido"}</Aviso>
        ) : null}
        {envio?.estado === "enviado" ? <Aviso tono="ok">Invitación enviada.</Aviso> : null}

        <Campo
          etiqueta="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="usuario@ejemplo.com"
        />

        <div className="space-y-1">
          <label htmlFor="rol" className="block text-neutral-600 text-sm">
            Rol
          </label>
          <select
            id="rol"
            value={rol}
            onChange={(e) => setRol(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          >
            {rolesAsignables.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <p className="text-neutral-500 text-xs">
            Solo aparecen los roles cuyos permisos tenés (ADR 0019).
          </p>
        </div>

        <div className="flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cerrar
          </Boton>
          <Boton onClick={enviar} disabled={enviando || email.trim().length === 0 || !rol}>
            {enviando ? "Enviando..." : "Invitar"}
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/** Datos de contacto + roles — porteada de `UserDetailPage`. */

/** Avatar con la inicial, nombre y email — la primera columna de allá. */
function CeldaUsuario({ miembro, esUnoMismo }: { miembro: Miembro; esUnoMismo: boolean }) {
  const inicial = (miembro.nombre.charAt(0) || miembro.email.charAt(0)).toUpperCase();
  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 font-bold text-sky-700 text-sm"
      >
        {inicial}
      </span>
      <div className="min-w-0">
        <div className="truncate font-medium">
          {miembro.clase === "invitacion" ? miembro.email : miembro.nombre}
          {esUnoMismo ? (
            <span className="ml-2 font-normal text-neutral-500 text-xs">(vos)</span>
          ) : null}
        </div>
        {miembro.clase === "usuario" ? (
          <div className="truncate text-neutral-500 text-sm">{miembro.email}</div>
        ) : (
          // Cuánto le queda al enlace: una invitación vencida es la que hay que
          // reenviar, y sin esto no se distingue de una recién mandada.
          <div className="text-neutral-500 text-xs">
            Expira {faltan(miembro.invitacion?.expiraEn ?? "")}
          </div>
        )}
      </div>
    </div>
  );
}
